// DISABLED: premium unlock for teachers is not shipping — see `isBelowReadingThreshold` below.
// import { TEACHER_READING_MIN_PREMIUM_STUDENTS } from '@package/api/schemas'

/** Fallback seat count when a school has no students and no trial pool. */
export const FALLBACK_SEAT_COUNT = 8

/** What `AssignSeatDialog` confirms and `NewForm` charges for: chosen children plus seats left empty. */
export interface SeatAssignmentValue {
  childIds: string[]
  emptySeats: number
}

export type SeatLockReason = 'premium' | 'scholarship' | null

export interface FlavouredChild {
  id: string
  heldSeatId: string | null
  heldSeatSourceType: string | null
  heldSeatOrganizationId: string | null
  heldSeatIsCommercial: boolean
  premiumId: string | null
  familyPremiumId: string | null
}

/**
 * Why a student cannot be picked, or null if they can.
 *
 * A locked student is left OUT of `AssignSeatDialog` entirely rather than listed unclickable, so
 * the reason is no longer rendered anywhere — it stays a discriminated result because the two arms
 * are the seat rules themselves, and a caller that does want to explain an exclusion needs them.
 *
 * Only a free-trial seat owned by THIS organization is replaceable — fulfilment releases it.
 * Every other grant flavour (sponsored_scholarship, pilot, internal, pemda) is locked, as is
 * any commercial seat, and so is a seat owned by someone else (a parent's family subscription
 * or another school), which this purchase has no right to disturb.
 */
export function getSeatLockReason(child: FlavouredChild, organizationId: string): SeatLockReason {
  if (child.heldSeatId === null) return null

  const isOwnFreeTrial = child.heldSeatSourceType === 'free_trial' && child.heldSeatOrganizationId === organizationId

  if (isOwnFreeTrial) return null

  return child.heldSeatIsCommercial ? 'premium' : 'scholarship'
}

/**
 * The seat count the dialog opens with.
 *
 * 1. Any eligible students -> all of them, none empty.
 * 2. Otherwise an active free-trial pool -> mirror it: its seated students selected, its
 *    unassigned seats carried into the stepper.
 * 3. Otherwise a flat fallback.
 */
export function getDefaultAssignment(args: {
  eligibleIds: string[]
  trialPoolSeatsTotal: number | null
  trialPoolSeatsUsed: number | null
}): { childIds: string[]; emptySeats: number } {
  const { eligibleIds, trialPoolSeatsTotal, trialPoolSeatsUsed } = args

  if (eligibleIds.length > 0) {
    return { childIds: eligibleIds, emptySeats: 0 }
  }

  if (trialPoolSeatsTotal !== null && trialPoolSeatsUsed !== null) {
    return { childIds: [], emptySeats: Math.max(0, trialPoolSeatsTotal - trialPoolSeatsUsed) }
  }

  return { childIds: [], emptySeats: FALLBACK_SEAT_COUNT }
}

/**
 * Who this purchase may seat, split the way the default ladder consumes it.
 *
 * `eligibleIds` holds children with no seat at all — brand new. `ownTrialIds` holds children sitting
 * on THIS organization's own free-trial seat: offerable, because fulfilment releases that seat, but
 * deliberately NOT "eligible" in the ladder's sense. A roster made only of trial holders must take
 * the ladder's trial-pool branch (mirror the pool: its holders selected, its spare seats carried),
 * not the all-eligible branch. The two lists are disjoint by construction.
 */
export function partitionOfferableChildren(
  children: FlavouredChild[],
  organizationId: string,
): { eligibleIds: string[]; ownTrialIds: string[] } {
  const eligibleIds: string[] = []
  const ownTrialIds: string[] = []

  for (const child of children) {
    if (child.heldSeatId === null) eligibleIds.push(child.id)
    else if (getSeatLockReason(child, organizationId) === null) ownTrialIds.push(child.id)
  }

  return { eligibleIds, ownTrialIds }
}

/**
 * The full default ladder: `getDefaultAssignment` over the eligible set, with this organization's own
 * trial holders folded into the selection whichever branch it took.
 *
 * Shared by `NewForm`, which settles the card's "Total kursi" from it before the dialog is ever
 * opened, and by `AssignSeatDialog`, which falls back to it when opened before the form has settled.
 * One function, so the two cannot drift.
 */
export function resolveDefaultSeatAssignment(args: {
  children: FlavouredChild[]
  organizationId: string
  trialPool: { seatsTotal: number; seatsUsed: number } | null
}): SeatAssignmentValue {
  const { eligibleIds, ownTrialIds } = partitionOfferableChildren(args.children, args.organizationId)

  const base = getDefaultAssignment({
    eligibleIds,
    trialPoolSeatsTotal: args.trialPool?.seatsTotal ?? null,
    trialPoolSeatsUsed: args.trialPool?.seatsUsed ?? null,
  })

  return { childIds: [...base.childIds, ...ownTrialIds], emptySeats: base.emptySeats }
}

/**
 * The deep-link variant (`/checkout/new?premium_seats=N`): the default offerable selection,
 * brand-new children first, truncated to `total`, the remainder as empty seats — so the page always
 * shows exactly the count it was linked with, never more.
 */
export function truncateDefaultSeatAssignment(args: {
  children: FlavouredChild[]
  organizationId: string
  total: number
}): SeatAssignmentValue {
  const { eligibleIds, ownTrialIds } = partitionOfferableChildren(args.children, args.organizationId)
  const childIds = [...eligibleIds, ...ownTrialIds].slice(0, args.total)

  return { childIds, emptySeats: Math.max(0, args.total - childIds.length) }
}

/**
 * Projected CAPACITY once this purchase is fulfilled — what the school gates will count against.
 *
 * NOT `selected.length`, and not a reach count. Two things move the school's number, and one does
 * not:
 * - A student with no seat at all: +1, a seat the organization did not own before.
 * - An EMPTY seat bought for later: +1 as well. Capacity counts owned seats whether or not a child
 *   sits on them, so seats parked for next term's intake move the bar the day they are paid for.
 * - This organization's own free-trial holder: +0. Fulfilment releases their trial seat and mints
 *   the bought one in its place, so the organization owns exactly as many seats afterwards.
 *
 * Children seated by anyone else never reach this function: they are locked out of the picker.
 */
export function getProjectedCapacity(args: {
  currentCapacity: number
  selected: { id: string; heldSeatId: string | null }[]
  emptySeats: number
}): number {
  const newlySeated = args.selected.filter((child) => child.heldSeatId === null).length

  return args.currentCapacity + newlySeated + args.emptySeats
}

// DISABLED: the premium unlock for teachers is not shipping, so there is no reading threshold to
// project against. Commented out, not deleted, so it can come back with the feature.
// export function isBelowReadingThreshold(projectedCapacity: number): boolean {
//   return projectedCapacity < TEACHER_READING_MIN_PREMIUM_STUDENTS
// }
